import axios from 'axios';
import moment from 'moment-timezone';
import { createSlice } from '@reduxjs/toolkit';

import { ALL, DEFAULT_ANALYTICS_PAGE_SIZE, API_LONG_REQUEST_TIMEOUT_MS } from '../constants/AppConstants';

import { setError } from './uiReducer';

// SLICE
const analyticsSlice = createSlice({
  name: 'ANALYTICS',
  initialState: {
    closedConversations: {},
    openConversations: {},
    pageLoading: true,
    isClosedActivitiesLoading: true,
    isOpenActivitiesLoading: true,
    isCampaignsActivitiesLoading: true,
    loading: false,
  },
  reducers: {
    receiveData: (state, action) => ({
      ...state,
      messages: [
        ...action.payload.messages,
      ],
      contacts: {
        ...action.payload.contacts,
      },
      responseTime: {
        ...action.payload.responseTime,
      },
      peakTime: {
        ...action.payload.peakTime,
      },
      pageLoading: false,
    }),
    receiveClosedConversations: (state, action) => ({
      ...state,
      closedConversations: {
        ...action.payload,
      },
      isClosedActivitiesLoading: false,
    }),
    receiveOpenConversations: (state, action) => ({
      ...state,
      openConversations: {
        ...action.payload,
      },
      isOpenActivitiesLoading: false,
    }),
    receiveCampaignsConversations: (state, action) => ({
      ...state,
      campaignsConversations: {
        ...action.payload,
      },
      isCampaignsActivitiesLoading: false,
    }),
    receiveCampaignsFailedContacts: (state, action) => ({
      ...state,
      campaignsFailedContacts: {
        ...action.payload,
      },
      isCampaignsFailedContactsLoading: false,
    }),
    requestData: (state) => ({
      ...state,
      pageLoading: true,
    }),
    requestClosedConversations: (state) => ({
      ...state,
      isClosedActivitiesLoading: true,
    }),
    requestOpenConversations: (state) => ({
      ...state,
      isOpenActivitiesLoading: true,
    }),
    requestCampaignsConversations: (state) => ({
      ...state,
      isCampaignsActivitiesLoading: true,
    }),
    requestCampaignsFailedContacts: (state) => ({
      ...state,
      isCampaignsFailedContactsLoading: true,
    }),
  },
});

export default analyticsSlice.reducer;

// ACTIONS
export const {
  receiveData,
  receiveClosedConversations,
  receiveOpenConversations,
  receiveCampaignsConversations,
  receiveCampaignsFailedContacts,
  requestData,
  requestClosedConversations,
  requestOpenConversations,
  requestCampaignsConversations,
  requestCampaignsFailedContacts,
} = analyticsSlice.actions;

// THUNKS -- ASYNC ACTION CREATORS
export function fetchAnalyticsData(fromDate, toDate) {
  return (dispatch) => {
    dispatch(requestData());
    return axios.all([
      fetchMessageData(fromDate, toDate),
      fetchContactData(fromDate, toDate),
      fetchResponseTime(fromDate, toDate),
      fetchPeakTime(fromDate, toDate),
    ])
      .then(axios.spread((messages, contacts, responseTime, peakTime) => {
        dispatch(receiveData(
          getAnalyticsPayload(
            messages,
            contacts,
            responseTime,
            peakTime,
          ),
        ));
      }))
      .catch((err) => {
        console.error(err.response || err);

        dispatch(setError(err.response || err));
      });
  };
}

export function getAnalyticsPayload(messages, contacts, responseTime, peakTime) {
  return {
    messages: messages.data,
    contacts: contacts.data,
    responseTime: responseTime.data,
    peakTime: peakTime.data,
  };
}

export function fetchMessageData(fromDate, toDate) {
  return axios.get(`/analytics/messages?from=${fromDate}&to=${toDate}`, { timeout: API_LONG_REQUEST_TIMEOUT_MS })
    .catch((err) => console.error(err.response || err));
}

export function fetchContactData(fromDate, toDate) {
  return axios.get(`/analytics/contacts?from=${fromDate}&to=${toDate}`, { timeout: API_LONG_REQUEST_TIMEOUT_MS })
    .catch((err) => console.error(err.response || err));
}

export function fetchPeakTime(fromDate, toDate) {
  return axios.get(`/analytics/peaktime?from=${fromDate}&to=${toDate}`, { timeout: API_LONG_REQUEST_TIMEOUT_MS })
    .catch((err) => console.error(err.response || err));
}

export function fetchResponseTime(fromDate, toDate) {
  return axios.get(`/analytics/responseTime?from=${fromDate}&to=${toDate}`, { timeout: API_LONG_REQUEST_TIMEOUT_MS })
    .catch((err) => console.error(err.response || err));
}

export function fetchClosedConversations(fromDate, toDate, pageNo = 0, pageSize = DEFAULT_ANALYTICS_PAGE_SIZE, sortOrder, sortBy) {
  return (dispatch) => {
    dispatch(requestClosedConversations());
    return axios.get(`/analytics/closedConversations?from=${fromDate}&to=${toDate}&pageNo=${pageNo}&pageSize=${pageSize}&sortOrder=${sortOrder}&sortBy=${sortBy}`, { timeout: API_LONG_REQUEST_TIMEOUT_MS })
      .then((activities) => {
        dispatch(receiveClosedConversations(activities.data));
      })
      .catch((err) => {
        dispatch(setError(err.response || err));
      });
  };
}

export function fetchOpenConversations(pageNo = 0, pageSize = DEFAULT_ANALYTICS_PAGE_SIZE, filterBy = ALL, sortOrder, sortBy, filters = {}) {
  const {
    filterMemberIds: toUserIds = [],
    filterGroupIds: toUserGroupIds = [],
    filterTagIds: tagIds = [],
    timeOpen: { timeOpenStart, timeOpenEnd } = {},
    lastMessage: { lastMessageStart, lastMessageEnd } = {},
  } = filters;
  let filterQueryString = '';
  if (toUserIds.length > 0) {
    filterQueryString = `&toUserIds=${toUserIds}`;
  } else if (toUserGroupIds.length > 0) {
    filterQueryString = `&toUserGroupIds=${toUserGroupIds}`;
  } else if (tagIds.length > 0) {
    filterQueryString = `&tagIds=${tagIds}`;
  }

  const params = {
    timeOpenStart,
    timeOpenEnd,
    lastMessageStart,
    lastMessageEnd,
  };
  return (dispatch) => {
    dispatch(requestOpenConversations());
    return axios.get(`/analytics/openConversations?pageNo=${pageNo}&pageSize=${pageSize}&filterBy=${filterBy}&sortOrder=${sortOrder}&sortBy=${sortBy}${filterQueryString}`, {
      params,
    })
      .then((activities) => {
        dispatch(receiveOpenConversations(activities.data));
      })
      .catch((err) => {
        dispatch(setError(err.response || err));
      });
  };
}

export function fetchCampaignsFailedContacts(id) {
  return (dispatch) => {
    dispatch(requestCampaignsFailedContacts());
    return axios.get(`/analytics/bulkMessages/${id}?status=failed`, { timeout: API_LONG_REQUEST_TIMEOUT_MS })
      .then((activities) => {
        dispatch(receiveCampaignsFailedContacts(activities.data));
      })
      .catch((err) => {
        dispatch(setError(err.response || err));
      });
  };
}

// Integration Analytics

export function getLinerAnalytics(orgId, dateStr) {
  const utcDate = moment(dateStr).utc().format('YYYY-MM-DD');
  return axios.get(`${process.env.REACT_APP_LINER_BASE_URL}/analytics/${orgId}/${utcDate}`, { timeout: API_LONG_REQUEST_TIMEOUT_MS });
}

export function getFeederAnalytics(orgId, dateStr) {
  const utcDate = moment(dateStr).utc().format('YYYY-MM-DD');
  return axios.get(`${process.env.REACT_APP_FEEDER_BASE_URL}/analytics/${orgId}/${utcDate}`, { timeout: API_LONG_REQUEST_TIMEOUT_MS });
}

export function getApiAnalytics(orgId, dateStr) {
  const utcDate = moment(dateStr).utc().format('YYYY-MM-DD');
  return axios.get(`/integrations/rhinoapiData?orgId=${orgId}&date=${utcDate}`);
}
